/**
 * Paquete de fotos de monstruos "VANGUARD_MOBS_LIMPIOS" (10 láminas JPG de 1536 × 1536, cuadrícula 4 × 4,
 * celdas de 384 px). Cada foto se asocia al ID EXACTO de 06_monsters impreso bajo ella en la lámina.
 *
 * - Las fotos del staff tienen prioridad: este paquete solo completa monstruos sin imagen (ver utils/entityImages).
 * - El rectángulo [x, y, ancho, alto] es relativo a la lámina de 1536 px y excluye el rótulo de ID/nombre.
 *   Se aplica proporcionalmente si la lámina se sirve a otro tamaño.
 * - El índice VANGUARD_MOBS_INDICE_ID.json no llegó adjunto: source_page, match_method y retouched quedan
 *   pendientes. Los recortes se midieron sobre las láminas recibidas.
 * - Una foto identifica a la entidad; no confirma drops, estadísticas ni ubicaciones.
 */
export type PhotoRect = [number, number, number, number]

export const MONSTER_SHEET_SIZE = 1536
const CELL = 384
/** Margen interior para no tocar nunca la celda vecina. */
const INSET = 2

export const monsterSheets: Record<number, string> = {
  1: 'https://cdn.magicpatterns.com/uploads/wvZhVHs6Dov9eUnTDeCa4g/VANGUARD_MOBS_LIMPIOS_01.jpg',
  2: 'https://cdn.magicpatterns.com/uploads/sBceQgRGxrCnxPXUARHNkG/VANGUARD_MOBS_LIMPIOS_02.jpg',
  3: 'https://cdn.magicpatterns.com/uploads/fyLZcxtYsYFYQsZznVmW5g/VANGUARD_MOBS_LIMPIOS_03.jpg',
  4: 'https://cdn.magicpatterns.com/uploads/9NextfqSKzgyNbpN6p97As/VANGUARD_MOBS_LIMPIOS_04.jpg',
  5: 'https://cdn.magicpatterns.com/uploads/4dotHN2EYeXg5wWjX5VqXm/VANGUARD_MOBS_LIMPIOS_05.jpg',
  6: 'https://cdn.magicpatterns.com/uploads/1uxPvNamEEziZR8jDz4MtA/VANGUARD_MOBS_LIMPIOS_06.jpg',
  7: 'https://cdn.magicpatterns.com/uploads/4A5Dk1zmuBKQP3b7o2Yj3p/VANGUARD_MOBS_LIMPIOS_07.jpg',
  8: 'https://cdn.magicpatterns.com/uploads/cZKspq2THLRGbcDDhux6KP/VANGUARD_MOBS_LIMPIOS_08.jpg',
  9: 'https://cdn.magicpatterns.com/uploads/gPeZY5XrjM3WLbykgiFXtd/VANGUARD_MOBS_LIMPIOS_09.jpg',
  10: 'https://cdn.magicpatterns.com/uploads/eTEkHShwVZ3mS9gJDewGfx/VANGUARD_MOBS_LIMPIOS_10.jpg',
}

/** [ID, nombre impreso] por lámina, fila a fila (4 por fila). */
const layout: Record<number, [number, string][]> = {
  1: [[0, 'Bull Fighter'], [1, 'Hound'], [2, 'Budge Dragon'], [3, 'Spider'], [4, 'Elite Bull Fighter'], [5, 'Hell Hound'], [6, 'Lich'], [7, 'Giant'], [8, 'Poison Bull Fighter'], [9, 'Thunder Lich'], [10, 'Dark Knight'], [11, 'Ghost'], [13, 'Hell Spider'], [14, 'Skeleton'], [15, 'Skeleton Archer'], [17, 'Cyclops']],
  2: [[18, 'Gorgon'], [19, 'Yeti'], [20, 'Elite Yeti'], [21, 'Assassin'], [22, 'Ice Monster'], [23, 'Hommerd'], [24, 'Worm'], [25, 'Ice Queen'], [26, 'Goblin'], [27, 'Chain Scorpion'], [28, 'Beetle Monster'], [29, 'Hunter'], [30, 'Forest Monster'], [31, 'Agon'], [32, 'Stone Golem'], [33, 'Elite Goblin']],
  3: [[34, 'Cursed Wizard'], [35, 'Death Gorgon'], [36, 'Shadow'], [37, 'Devil'], [38, 'Balrog'], [39, 'Poison Shadow'], [40, 'Death Knight'], [41, 'Death Cow'], [42, 'Red Dragon'], [43, 'Golden Budge Dragon'], [45, 'Bahamut'], [46, 'Vepar'], [47, 'Valkyrie'], [48, 'Lizard King'], [49, 'Hydra'], [51, 'Great Bahamut']],
  4: [[52, 'Silver Valkyrie'], [53, 'Golden Titan'], [55, 'Death King'], [57, 'Iron Wheel'], [58, 'Tantalos'], [59, 'Zaikan'], [60, 'Bloody wolf'], [61, 'Beam Knight'], [62, 'Mutant'], [63, 'Death Beam Knight'], [69, 'Alquamos'], [70, 'Queen Rainier'], [71, 'Mega Crust'], [72, 'Phantom Knight'], [73, 'Drakan'], [74, 'Alpha Crust']],
  5: [[75, 'Great Drakan'], [77, 'Phoenix of Darkness'], [78, 'Golden Goblin'], [80, 'Golden Lizard King'], [81, 'Golden Vepar'], [82, 'Golden Tantalos'], [83, 'Golden Wheel'], [135, 'White wizard'], [136, 'Destructive ogre soldier'], [137, 'Destructive ogre archer'], [275, 'Kundun'], [290, 'Lizard Warrior'], [291, 'Fire Golem'], [292, 'Queen Bee'], [293, 'Poison Golem'], [294, 'Ax Warrior']],
  6: [[295, 'Erohim'], [304, 'Witch Queen'], [305, 'Blue Golem'], [306, 'Death Rider'], [307, 'Forest Orc'], [308, 'Death Tree'], [309, 'Hell Maine'], [310, 'Hammer Scout'], [311, 'Lance Scout'], [312, 'Bow Scout'], [314, 'Scout(Hero)'], [315, 'Werewolf(Hero)'], [349, 'Balgass'], [351, 'Splinter Wolf'], [352, 'Iron Rider'], [353, 'Satyros']],
  7: [[354, 'Blade Hunter'], [355, 'Kentauros'], [357, 'Genocider'], [359, 'Twin Tale'], [361, 'Nightmare'], [365, 'Pouch of Blessing'], [418, 'Strange Rabbit'], [419, 'Hideous Rabbit'], [421, 'Polluted Butterfly'], [422, 'Cursed Lich'], [423, 'Totem Golem'], [424, 'Grizzly'], [425, 'Captain Grizzly'], [444, 'Shadow Pawn'], [445, 'Shadow Knight'], [446, 'Shadow Look']],
  8: [[447, 'Thunder Napin'], [448, 'Ghost Napin'], [449, 'Blaze Napin'], [455, 'Giant Mammoth'], [456, 'Ice Giant'], [457, 'Coolutin'], [458, 'Iron Knight'], [459, 'Selupan'], [463, 'Fire Flame Ghost'], [486, 'Blood Assassin'], [489, 'Burning Lava Giant'], [493, 'Golden Dark Knight'], [494, 'Golden Devil'], [495, 'Golden Stone Golem'], [496, 'Golden Crust'], [497, 'Golden Satyros']],
  9: [[498, 'Golden Twin Tail'], [499, 'Golden Iron Knight'], [501, 'Great Golden Dragon'], [502, 'Golden Rabbit'], [549, 'Bloody Orc'], [550, 'Bloody Death Rider'], [551, 'Bloody Golem'], [552, 'Bloody Witch Queen'], [553, 'Berserker Warrior'], [554, 'Kentauros Warrior'], [555, 'Gigantis Warrior'], [556, 'Genocider Warrior'], [558, 'Ice Napin'], [559, 'Shadow Master'], [561, 'Medusa'], [562, 'Dark Mammoth']],
  10: [[563, 'Dark Giant'], [564, 'Dark Coolutin'], [569, 'Venomous Chain Scorpion'], [570, 'Bone Scorpion'], [571, 'Orcus'], [572, 'Gollock'], [573, 'Crypta'], [574, 'Crypos'], [575, 'Condra'], [576, 'Narcondra']],
}

/** Fotos que no ocupan la franja apaisada estándar de su celda (medidas sobre la lámina). */
const rawOverrides: Record<number, PhotoRect> = {
  42: [20, 768, 344, 344],
  43: [384, 770, 384, 338],
  53: [432, 0, 287, 344],
  80: [1172, 0, 344, 344],
  81: [20, 384, 344, 344],
  82: [384, 418, 384, 276],
  83: [773, 384, 374, 344],
  135: [1228, 384, 232, 344],
  136: [38, 768, 307, 344],
  137: [429, 768, 293, 344],
  365: [404, 384, 344, 344],
  463: [20, 768, 344, 344],
  496: [817, 1152, 286, 344],
  499: [404, 0, 344, 344],
  502: [1152, 64, 384, 244],
}

/**
 * Encuadre por ID, aplicado DENTRO del recorte de su foto (nunca fuera de él).
 * fx / fy = centro del ejemplar elegido, como fracción del ancho / alto de la foto.
 * zoom = 1 → cuadro cuadrado del lado menor de la foto; > 1 acerca. Revisado visualmente sobre las 10 láminas.
 * Sin entrada: centrado, zoom 1.
 */
export type PhotoFocus = { fx: number; fy: number; zoom: number; note?: string }

export const photoFocus: Record<number, PhotoFocus> = {
  1: { fx: 0.78, fy: 0.55, zoom: 1.1, note: 'Dos ejemplares: se enfoca el de la derecha (el de la izquierda está cortado arriba)' },
  2: { fx: 0.6, fy: 0.46, zoom: 1.05 },
  3: { fx: 0.5, fy: 0.5, zoom: 1.4 },
  6: { fx: 0.62, fy: 0.5, zoom: 1, note: 'Se deja fuera el segundo Lich del borde inferior izquierdo' },
  8: { fx: 0.42, fy: 0.5, zoom: 1 },
  14: { fx: 0.68, fy: 0.55, zoom: 1.15, note: 'Dos ejemplares superpuestos: se enfoca el de la derecha' },
  24: { fx: 0.76, fy: 0.46, zoom: 1.22, note: 'Dos ejemplares en los extremos: se enfoca el de la derecha, completo y centrado' },
  26: { fx: 0.55, fy: 0.55, zoom: 1.6 },
  27: { fx: 0.47, fy: 0.45, zoom: 1.2 },
  28: { fx: 0.78, fy: 0.45, zoom: 1.15, note: 'Dos ejemplares: se enfoca el de la derecha' },
  30: { fx: 0.63, fy: 0.5, zoom: 1, note: 'Se enfoca el ejemplar de la derecha' },
  40: { fx: 0.6, fy: 0.5, zoom: 1 },
  46: { fx: 0.6, fy: 0.5, zoom: 1, note: 'Se deja fuera la criatura cortada del borde izquierdo' },
  48: { fx: 0.65, fy: 0.5, zoom: 1 },
  51: { fx: 0.42, fy: 0.5, zoom: 1 },
  62: { fx: 0.66, fy: 0.5, zoom: 1.1, note: 'Dos ejemplares superpuestos: se enfoca el de la derecha' },
  75: { fx: 0.58, fy: 0.5, zoom: 1 },
  290: { fx: 0.52, fy: 0.45, zoom: 1.5 },
  291: { fx: 0.5, fy: 0.5, zoom: 1.5 },
  292: { fx: 0.5, fy: 0.45, zoom: 1.6 },
  293: { fx: 0.5, fy: 0.53, zoom: 1.5 },
  294: { fx: 0.5, fy: 0.52, zoom: 1.3 },
  304: { fx: 0.42, fy: 0.5, zoom: 1 },
  308: { fx: 0.62, fy: 0.5, zoom: 1.05, note: 'Varios árboles juntos: se enfoca el de la derecha' },
  354: { fx: 0.72, fy: 0.5, zoom: 1.2, note: 'Dos ejemplares juntos: se enfoca el de la derecha' },
  361: { fx: 0.6, fy: 0.5, zoom: 1 },
  418: { fx: 0.53, fy: 0.48, zoom: 1.7 },
  419: { fx: 0.45, fy: 0.5, zoom: 1 },
  421: { fx: 0.68, fy: 0.72, zoom: 1.5, note: 'Dos ejemplares: se enfoca el de abajo a la derecha' },
  422: { fx: 0.28, fy: 0.5, zoom: 1.1, note: 'Dos ejemplares: se enfoca el de la izquierda (el otro está cortado)' },
  424: { fx: 0.42, fy: 0.5, zoom: 1 },
  425: { fx: 0.74, fy: 0.5, zoom: 1.1, note: 'Dos ejemplares juntos: se enfoca el de la derecha' },
  458: { fx: 0.36, fy: 0.5, zoom: 1 },
  552: { fx: 0.42, fy: 0.5, zoom: 1 },
  561: { fx: 0.45, fy: 0.5, zoom: 1, note: 'Se deja fuera el personaje del borde derecho' },
  570: { fx: 0.3, fy: 0.5, zoom: 1.05, note: 'Dos ejemplares superpuestos: se enfoca el de la izquierda' },
  571: { fx: 0.4, fy: 0.5, zoom: 1 },
}

/** Fotos donde no se puede aislar un ejemplar sin que asome otro: conviene reemplazarlas. */
export const photosNeedingReplacement: { id: number; reason: string }[] = [
  { id: 14, reason: 'Dos Skeleton superpuestos; la espada del otro ejemplar cruza el encuadre' },
  { id: 62, reason: 'Dos Mutant superpuestos; asoma parte del otro ejemplar' },
  { id: 308, reason: 'Varios Death Tree juntos; no hay un ejemplar separado' },
  { id: 354, reason: 'Dos Blade Hunter pegados; asoma el borde del otro' },
  { id: 425, reason: 'Dos Captain Grizzly pegados; asoma el brazo del otro' },
  { id: 570, reason: 'Dos Bone Scorpion superpuestos; asoman patas del otro' },
  { id: 365, reason: 'La captura incluye el rótulo del juego "Pouch of Blessing" sobre el objeto' },
  { id: 463, reason: 'La captura incluye el rótulo del juego "Fire Flame Ghost" sobre el objeto' },
]

export type MonsterPhoto = { id: number; sheet: number; rect: PhotoRect; sourceName: string; focus: PhotoFocus }

const inset = ([x, y, w, h]: PhotoRect): PhotoRect => [x + INSET, y + INSET, w - INSET * 2, h - INSET * 2]
const DEFAULT_FOCUS: PhotoFocus = { fx: 0.5, fy: 0.5, zoom: 1 }

export const monsterPhotos: MonsterPhoto[] = Object.entries(layout).flatMap(([sheet, list]) =>
  list.map(([id, sourceName], i) => {
    const col = i % 4
    const row = Math.floor(i / 4)
    const base: PhotoRect = rawOverrides[id] ?? [col * CELL, row * CELL + 64, CELL, 216]
    return { id, sheet: Number(sheet), rect: inset(base), sourceName, focus: photoFocus[id] ?? DEFAULT_FOCUS }
  }),
)

/** Cuadro cuadrado final [x, y, lado] en coordenadas de la lámina, siempre dentro del recorte de la foto. */
export function focusSquare(rect: PhotoRect, focus: PhotoFocus = DEFAULT_FOCUS): [number, number, number] {
  const [x, y, w, h] = rect
  const side = Math.min(w, h) / Math.max(1, focus.zoom)
  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi)
  const sx = clamp(x + focus.fx * w - side / 2, x, x + w - side)
  const sy = clamp(y + focus.fy * h - side / 2, y, y + h - side)
  return [sx, sy, side]
}

/** El rectángulo cae dentro de la lámina y de su propia celda (sin invadir vecinas ni el rótulo). */
export function rectInBounds(p: MonsterPhoto): boolean {
  const [x, y, w, h] = p.rect
  if (x < 0 || y < 0 || w <= 0 || h <= 0 || x + w > MONSTER_SHEET_SIZE || y + h > MONSTER_SHEET_SIZE) return false
  const cellX = Math.floor((x + w / 2) / CELL) * CELL
  const cellY = Math.floor((y + h / 2) / CELL) * CELL
  return x >= cellX && x + w <= cellX + CELL && y >= cellY && y + h <= cellY + 350
}
